import React, { useCallback, useMemo } from 'react';

import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';

import { Alert, Box, Button, Chip, CircularProgress, Container, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import PageHeader from '~/components/Common/Elements/PageHeader';
import QuickSearchToolbar from '~/components/Common/Elements/QuickSearchToolbar';

import { useGetAllCampaigns } from '~/hooks/apis/accommodation/useCampaigns';
import useAppNavigation from '~/hooks/useAppNavigation';

import { Campaign } from '~/services/AccommodationService';

import { campaignStatusPillProps } from './constants';

const columns: Array<GridColDef> = [
  {
    field: 'name',
    headerName: 'Campaign Name',
    flex: 2,
    minWidth: 200,
  },
  {
    field: 'id',
    headerName: 'Campaign ID',
    flex: 1,
    minWidth: 280,
  },
  {
    field: 'displayName',
    headerName: 'Display Name',
    flex: 1,
    minWidth: 180,
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 120,
    renderCell: ({ value }) => {
      const { label, color } = campaignStatusPillProps(value as Campaign['status']);
      return <Chip label={label} color={color} size="small" variant="outlined" />;
    },
  },
  {
    field: 'propertyIds',
    headerName: 'Properties',
    width: 100,
    valueGetter: (_value, row) => row.propertyIds?.length ?? 0,
  },
];

export default function AccommodationCampaignsContainer() {
  const { push } = useAppNavigation();
  const { data, isLoading, error } = useGetAllCampaigns();
  const { search } = useLocation();

  // Seed the quick filter from a `?q=` param so a campaign/property id can be deep-linked
  // straight into a filtered list, mirroring the Bundles list.
  const initialQuickFilterValues = useMemo(() => {
    const q = new URLSearchParams(search).get('q')?.trim();
    return q ? q.split(/\s+/) : [];
  }, [search]);

  const handleRowClick = useCallback(
    (params: { row: Campaign }) => {
      push(`/accommodation/campaigns/${params.row.id}`);
    },
    [push],
  );

  const handleCreate = useCallback(() => {
    push('/accommodation/campaigns/new');
  }, [push]);

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>Campaigns</title>
      </Helmet>
      <PageHeader title="Campaigns" />

      <Box mt={2}>
        {error && (
          <Alert severity="error">
            <Typography>Failed to load campaigns: {error.message}</Typography>
          </Alert>
        )}

        {!error && (
          <>
            <Box display="flex" justifyContent="flex-end" mb={2}>
              <Button variant="contained" onClick={handleCreate}>
                + New Campaign
              </Button>
            </Box>

            {isLoading && (
              <Box p={4} display="flex" justifyContent="center">
                <CircularProgress variant="indeterminate" />
              </Box>
            )}

            {!isLoading && (
              <DataGrid
                rows={data?.campaigns ?? []}
                columns={columns}
                pageSizeOptions={[10, 25, 50]}
                initialState={{
                  pagination: { paginationModel: { pageSize: 25 } },
                  filter: {
                    filterModel: {
                      items: [],
                      quickFilterExcludeHiddenColumns: false,
                      quickFilterValues: initialQuickFilterValues,
                    },
                  },
                }}
                onRowClick={handleRowClick}
                autoHeight
                disableRowSelectionOnClick
                slots={{ toolbar: QuickSearchToolbar }}
                sx={{ cursor: 'pointer' }}
              />
            )}
          </>
        )}
      </Box>
    </Container>
  );
}
